import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Pin } from 'lucide-react';
import { useSiteImage } from '../lib/siteAssets';
const LOGO_DEFAULT = 'https://customer-assets.emergentagent.com/job_studio-italy-demo/artifacts/mdb7ax1d_background_removed_image_Xr_VnmpfTaKxDawp8l6Yig.png';

const Footer = () => {
  const logoSrc = useSiteImage('logo', LOGO_DEFAULT);
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    products: [
      { name: 'Italy Relocation Kit', path: '/shop' },
      { name: 'Visa Guides', path: '/shop' },
      { name: 'Pet Relocation', path: '/shop' },
      { name: 'Free Resources', path: '/freebie' },
    ],
    company: [
      { name: 'About', path: '/about' },
      { name: 'Blog', path: '/blog' },
      { name: 'Contact', path: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Refund Policy', path: '/refunds' },
    ],
    sisterBrands: [
      { name: 'PB Travel Angel', url: 'https://pbtravelangel.com' },
      { name: 'CloudMyLuxury', url: 'https://cloudmyluxury.com' },
    ],
  };

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/theexpatstudio' },
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/theexpatstudio' },
    { name: 'TikTok', icon: Twitter, url: 'https://tiktok.com/@theexpatstudio' },
    { name: 'Pinterest', icon: Pin, url: 'https://pinterest.com/theexpatstudio' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#F5EFE9] text-[#1E1622] border-t border-[#C5A059]/40">
      <div className="container-custom pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img
                src={logoSrc}
                alt="The Expat Studio"
                className="h-20 md:h-24 w-auto object-contain"
              />
            </Link>
            <p className="text-[#1E1622]/60 text-sm leading-relaxed mb-6 max-w-sm">
              Beautiful, comprehensive relocation kits that guide you through every step —
              from visas and finances to finding your new home and settling your pets.
            </p>
            <p className="font-display text-xl italic text-[#3B164C]">
              "Your move, beautifully planned."
            </p>
            {/* Social Links */}
            <div className="flex items-center space-x-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${social.name.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border border-[#C5A059]/40 bg-white text-[#3B164C] flex items-center justify-center transition-all duration-300 hover:bg-[#C5A059] hover:text-white hover:scale-110"
                  aria-label={social.name}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="overline mb-5">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-[#1E1622]/70 hover:text-[#C5A059] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="overline mb-5">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase()}`}
                    className="text-sm text-[#1E1622]/70 hover:text-[#C5A059] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li className="pt-4">
                <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">
                  Sister Brands
                </span>
              </li>
              {footerLinks.sisterBrands.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-[#1E1622]/70 hover:text-[#C5A059] transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="overline mb-5">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-[#1E1622]/70 hover:text-[#C5A059] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-[#C5A059]/25 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#1E1622]/50">
            © {currentYear} The Expat Studio. All rights reserved.
          </p>
          <p className="text-sm text-[#1E1622]/50">
            Made with love in Australia
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
